/* eslint-disable react/no-unknown-property */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import propTypes from "prop-types";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import ModalActions from "../../redux/reducers/modal.reducer";
import { formatAmount, formatAmount2 } from "../../lib/utils.js";
import AnalyticsActions from "../../redux/reducers/analytics.reducer";
import AnalyticsModel from "../../lib/models/analytics.model";
import { startInvestFlow } from "../../redux/epics/invest";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";
import leftBorder from "../../assets/images/left border.svg";
import infoTooltip from "../../assets/images/info-tooltip2.svg";
import "./MyBalance.scss";

const MyBalance = ({
  availableMoney,
  availableMoneyExchange,
  investments,
  investmentsExchange,
  generalBalance,
  debt,
  shares,
  convertibleNote,
  onShowInvestments
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const userIdValue = useSelector(({ user }) => user.userInfo.user.id);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleBack = () => {
    dispatch(push("/"));
  };
  const goToRecharge = () => {
    dispatch(startInvestFlow());
    dispatch(AnalyticsActions.trackEvent(AnalyticsModel.goToRecharge));
  };

  const goToWithdrawal = () => {
    if (Number(availableMoney) > 1) {
      dispatch(
        ModalActions.setConditionalModalState(true, "MoneyWithdrawalModal", {
          user_id: userIdValue
        })
      );
      dispatch(AnalyticsActions.trackEvent(AnalyticsModel.goToCancel));
    }
  };

  const { t } = useTranslation();

  const styledBalanceAmount = formatAmount2(generalBalance);
  const styledInvestAmount = formatAmount(investments);
  const styledInvestExchangeAmount = formatAmount(investmentsExchange);
  const styledAvailableAmount = formatAmount(availableMoney);
  const styledAvailableExchangeAmount = formatAmount(availableMoneyExchange);
  const styledDebt = formatAmount(debt);
  const styledShares = formatAmount(shares);
  const styledConvertibleNote = formatAmount(convertibleNote);

  const isMovements = location.pathname.includes("/my-movements");

  const handleInvestmentsClick = () => {
    if (isMovements) {
      dispatch(push("/my-balance"));
      return;
    }
    if (onShowInvestments) {
      onShowInvestments();
    }
    const section = document.getElementById("investments-section");
    if (section) {
      const top = section.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="balance-container">
      <div className="balance-content">
        <button id="balance-go-back" onClick={handleBack}>
          <div className="balance-content-button">
            <FontAwesomeIcon
              className="balance-content-button__icon"
              icon={faChevronLeft}
            />
            <p>Volver</p>
          </div>
        </button>

        <div className="balance">
          <div className="title-info">
            <div className="col">
              <div className="title">
                <img className="left-border" src={leftBorder} alt="" />
                <p className="title-balance">{t("header.balance")}</p>
              </div>
              <p className="description-subtitle">{t("myBalance.subtitle")}</p>
            </div>
          </div>

          <div className="balance-disponible">
            <div className="cta-balance-disponible">
              {/* cta-1 */}
              <div className="cta-1">
                <div className="row-title">
                  <p>Balance</p>
                  <img
                    className="icon-info-tooltip"
                    src={infoTooltip}
                    alt="info"
                    data-tip="balance_tooltip"
                    data-for="balance_tooltip"
                  />
                  <ReactTooltip
                    id="balance_tooltip"
                    place="top"
                    effect="solid"
                    className="tooltip-figma"
                    backgroundColor="#62618B"
                    textColor="#FFFFFF"
                  >
                    Este valor corresponde a la suma de tus inversiones y el
                    disponible.
                  </ReactTooltip>
                </div>
                <div className="values">
                  <p className="currency">COP&nbsp;$&nbsp;</p>
                  <p className="amount">{styledBalanceAmount}</p>
                </div>
              </div>

              {/* cta-2 */}
              <div className="cta-2">
                <div className="info">
                  <div className="disponible">
                    <div className="head">
                      <p>{t("balance.availableCash")}</p>
                      <img
                        className="icon-info-tooltip"
                        src={infoTooltip}
                        alt="info"
                        data-tip="available_tooltip"
                        data-for="available_tooltip"
                      />
                      <ReactTooltip
                        id="available_tooltip"
                        place="top"
                        effect="solid"
                        className="tooltip-figma"
                        backgroundColor="#62618B"
                        textColor="#FFFFFF"
                      >
                        {t("myBalance.tooltip")}
                      </ReactTooltip>
                    </div>
                    <div className="value">
                      <p className="currency">COP&nbsp;</p>
                      <p className="amount">{styledAvailableAmount}</p>
                    </div>
                  </div>

                  <div className="por-confirmar">
                    <p className="title">{t("myBalance.availableExchange")}</p>
                    <div className="values">
                      <p className="currency">COP&nbsp;</p>
                      <p className="amount">{styledAvailableExchangeAmount}</p>
                    </div>
                  </div>
                </div>

                <div className="actions">
                  <button id="go-to-recharge" onClick={goToRecharge}>
                    Cargar
                  </button>
                  <p
                    id="go-to-withdrawals"
                    className="withdraw-link"
                    onClick={goToWithdrawal}
                    data-available={availableMoney}
                  >
                    Retirar dinero
                  </p>
                </div>
              </div>
            </div>

            {/* cta-mis-inversiones */}
            <div className="cta-mis-inversiones">
              <div className="row-wrapper">
                <div className="mis-inversiones">
                  <div className="data-block">
                    <div className="title-row main">
                      <p>{t("header.investments")}</p>
                      <img
                        className="icon-info-tooltip"
                        src={infoTooltip}
                        alt="info"
                        data-tip="investments_tooltip"
                        data-for="investments_tooltip"
                      />
                      <ReactTooltip
                        id="investments_tooltip"
                        place="top"
                        effect="solid"
                        className="tooltip-figma"
                        backgroundColor="#62618B"
                        textColor="#FFFFFF"
                      >
                        Este valor corresponde al saldo total de tus inversiones
                        en Deuda, Acciones y Notas Convertibles.
                      </ReactTooltip>
                    </div>
                    <div className="values-row main">
                      <p className="currency">COP&nbsp;</p>
                      <p className="amount">{styledInvestAmount}</p>
                    </div>
                  </div>
                  <div className="data-block">
                    <div className="title-row sub">
                      <p>{t("myBalance.investmentExchange")}</p>
                    </div>
                    <div className="values-row sub">
                      <p className="currency">COP&nbsp;</p>
                      <p className="amount">{styledInvestExchangeAmount}</p>
                    </div>
                  </div>
                </div>

                <div className="deuda">
                  <div className="data-block">
                    <div className="title-row section">
                      <p>{t("myBalance.investmentDebt")}</p>
                    </div>
                    <div className="values-row section">
                      <p className="currency">COP&nbsp;</p>
                      <p className="amount">{styledDebt}</p>
                    </div>
                  </div>
                </div>

                <div className="acciones">
                  <div className="data-block">
                    <div className="title-row section">
                      <p>{t("myBalance.investmentEquity")}</p>
                    </div>
                    <div className="values-row section">
                      <p className="currency">COP&nbsp;</p>
                      <p className="amount">{styledShares}</p>
                    </div>
                  </div>
                </div>

                <div className="notas">
                  <div className="data-block">
                    <div className="title-row section">
                      <p>Notas convertibles</p>
                    </div>
                    <div className="values-row section">
                      <p className="currency">COP&nbsp;</p>
                      <p className="amount">{styledConvertibleNote}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* controls */}
      {isMobileView ? (
        <div
          className="Controls Controls--mobile"
          style={{
            width: "100%",
            height: "100%",
            paddingLeft: 28,
            paddingRight: 28,
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 16,
            display: "inline-flex"
          }}
        >
          <div
            className="CardSubtab"
            style={{
              flex: "1 1 0",
              padding: 16,
              background: !isMovements ? "#0BBEF0" : "#F2F2F3",
              boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.04)",
              overflow: "hidden",
              borderRadius: 8,
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
              display: "flex",
              cursor: "pointer"
            }}
            onClick={handleInvestmentsClick}
          >
            <div
              style={{
                color: !isMovements ? "#F1F8FB" : "#475061",
                fontSize: 16,
                fontFamily: "Nunito",
                fontWeight: !isMovements ? "800" : "700",
                lineHeight: "24px",
                wordWrap: "break-word"
              }}
            >
              Mis inversiones
            </div>
          </div>
          <div
            className="CardSubtab"
            style={{
              flex: "1 1 0",
              height: 56,
              background: isMovements ? "#0BBEF0" : "#F2F2F3",
              boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.04)",
              overflow: "hidden",
              borderRadius: 8,
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
              display: "flex",
              cursor: "pointer"
            }}
            onClick={() => dispatch(push("/my-movements"))}
          >
            <div
              style={{
                color: isMovements ? "#F1F8FB" : "#475061",
                fontSize: 16,
                fontFamily: "Nunito",
                fontWeight: isMovements ? "800" : "700",
                lineHeight: "24px",
                wordWrap: "break-word"
              }}
            >
              Mis movimientos
            </div>
          </div>
        </div>
      ) : (
        <div
          className="Controls"
          style={{
            width: "100%",
            paddingTop: 24,
            paddingBottom: 24,
            justifyContent: "center",
            alignItems: "center",
            gap: 32,
            display: "inline-flex",
            border: "none",
            boxShadow: "none"
          }}
        >
          <div
            className="Content"
            style={{
              width: 1110,
              justifyContent: "flex-start",
              alignItems: "center",
              display: "flex"
            }}
          >
            <div
              className="Tags"
              style={{
                flex: "1 1 0",
                justifyContent: "center",
                alignItems: "center",
                gap: 24,
                display: "flex"
              }}
            >
              <div
                className="CardSubtab"
                style={{
                  width: 255,
                  height: 60,
                  background: !isMovements ? "#0BBEF0" : "#F2F2F3",
                  borderRadius: 8,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer"
                }}
                onClick={handleInvestmentsClick}
              >
                <div
                  style={{
                    textAlign: "center",
                    color: !isMovements ? "white" : "#475061",
                    fontSize: 20,
                    fontFamily: "Nunito",
                    fontWeight: "800",
                    wordWrap: "break-word"
                  }}
                >
                  Mis inversiones
                </div>
              </div>
              <div
                className="CardSubtab"
                style={{
                  width: 255,
                  height: 60,
                  background: isMovements ? "#0BBEF0" : "#F2F2F3",
                  borderRadius: 8,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer"
                }}
                onClick={() => dispatch(push("/my-movements"))}
              >
                <div
                  style={{
                    textAlign: "center",
                    color: isMovements ? "white" : "#475061",
                    fontSize: 20,
                    fontFamily: "Nunito",
                    fontWeight: "800",
                    wordWrap: "break-word"
                  }}
                >
                  Mis movimientos
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MyBalance.propTypes = {
  availableMoney: propTypes.number.isRequired,
  investments: propTypes.number.isRequired,
  availableMoneyExchange: propTypes.number.isRequired,
  investmentsExchange: propTypes.number.isRequired,
  generalBalance: propTypes.number.isRequired,
  debt: propTypes.number,
  shares: propTypes.number,
  convertibleNote: propTypes.number,
  onShowInvestments: propTypes.func
};

export default MyBalance;
